import { DialogContext } from "@/lib/main";
import { type ReactNode, useEffect, useRef, useState } from "react";

const DialogProvider = ({ children }: { children: ReactNode }) => {
  const [content, setContent] = useState<ReactNode>(null);
  const [showDialog, setShowDialog] = useState(false);

  const dialogRef = useRef<HTMLDialogElement>(null);
  const clearContentTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );

  const closeDialog = () => {
    const dialog = dialogRef.current;
    const wasOpen = Boolean(dialog?.open);

    if (clearContentTimeoutRef.current) {
      clearTimeout(clearContentTimeoutRef.current);
      clearContentTimeoutRef.current = null;
    }

    try {
      if (dialog?.open) {
        dialog.close();
      }
      dialog?.blur();
    } catch {
      // * swallow close race errors and still force state cleanup
    } finally {
      setShowDialog(false);

      // * wait for animation only if dialog was actually open
      if (wasOpen) {
        clearContentTimeoutRef.current = setTimeout(() => {
          setContent(null);
          clearContentTimeoutRef.current = null;
        }, 330);
      } else {
        setContent(null);
      }
    }
  };

  useEffect(() => {
    return () => {
      if (clearContentTimeoutRef.current) {
        clearTimeout(clearContentTimeoutRef.current);
      }
    };
  }, []);

  return (
    <DialogContext.Provider
      value={{
        showDialog,
        setShowDialog,
        content,
        setContent,
        dialogRef,
        closeDialog,
      }}
    >
      {children}
    </DialogContext.Provider>
  );
};

export default DialogProvider;
